import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTicket, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useRef, useState } from 'react';
import { MyOrder, FullSegment } from 'ts/shared/interfaces/MOResponse';
import useModalClass from 'hooks/useModalClass';
import { useTranslation } from 'react-i18next';
import { getStatus } from 'constants/mo';
import { getVoucher } from 'services/myOrders';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { showError } from 'utils/utils';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoVoucherMultiRoomProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (ids: string, thin: string) => void;
  myOrder: MyOrder;
  segment: FullSegment;
}

export const MoVoucherMultiRoom: FC<MoVoucherMultiRoomProps> = ({ myOrder, ...props }) => {
  const { t } = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(props.isOpen);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [isLoading, setIsLoading] = useState(false);
  const [thinReport, setThinReport] = useState('0');

  const [roomIds, setRoomIds] = useState(
    myOrder.fullSegment.rooms.map((room) => room.roomNumber).join(',')
  );

  const onClickSumit = async () => {
    setIsLoading(true);

    const voucher = await getVoucher(
      props.segment,
      props.segment.backOfficeExchangeVoucher,
      thinReport === '1',
      roomIds
    );

    setIsLoading(false);

    if (voucher) {
      props.onSuccess(roomIds, thinReport);
    } else {
      showError(t('something_went_wrong'));
    }
  };

  const onClickRoom = (roomId: string) => {
    if (roomIds.includes(roomId)) {
      setRoomIds(
        roomIds
          .split(',')
          .filter((id) => id !== roomId)
          .join(',')
      );
    } else {
      setRoomIds(roomIds + ',' + roomId);
    }
  };

  return (
    <Fragment>
      {props.isOpen && (
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && props.onClose()}
        >
          <div ref={modalRef} className={classList} style={modalStyle}>
            <div
              className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <div>
                <span className="title mr-10"> {t('mo.print_voucher')}</span>
                {myOrder?.fullSegment && (
                  <Fragment>
                    <span className="mr-5">{' '}-{' '}Order: {myOrder.orderRow.orderId}</span>
                    <span className="mr-5">Segment: {myOrder.fullSegment.orderSegId}</span>
                    {!!myOrder.orderRow.status && (
                      <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                        {myOrder.orderRow.status}
                      </span>
                    )}
                  </Fragment>
                )}
              </div>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => props.onClose()} />
              </div>
            </div>
            <div className="modal-content">
              {myOrder.fullSegment.rooms
                .filter((room) => room.sysStatus.code === 'OK')
                .map((room, index) => (
                  <div key={index} className="mb-10">
                    <Checkbox
                      label={room.roomNumber + '. ' + room.roomDisplay}
                      checked={roomIds.includes(room.roomNumber + '')}
                      onChange={() => onClickRoom(room.roomNumber + '')}
                      disabled={props.segment.roomsStatusCode !== 'OK'}
                    />
                  </div>
                ))}
              <div className="mb-30"></div>
              <SelectCustom
                options={[
                  { value: '0', label: t('mo.regular_voucher') },
                  { value: '1', label: t('mo.thin_voucher') },
                ]}
                value={thinReport}
                onChange={(value) => setThinReport(value)}
                movablePlaceholder={t('mo.voucher_type')}
                movablePlaceholderIcon={faTicket}
              />
            </div>

            <div className="mt-20 flex-between">
              <Button
                className="btn btn-default"
                title={t('close')}
                onClick={() => props.onClose()}
              />
              <Button
                className="btn btn-primary"
                title={t('confirm')}
                onClick={() => onClickSumit()}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(MoVoucherMultiRoom, 'MoVoucherMultiRoom');
